'use client';

import { c as _c } from "react/compiler-runtime";
import { TRANSPARENT_COLOR_CODE } from 'betterplace-utils';
import classNames from 'classnames';
import { type JSX, type KeyboardEventHandler, type MouseEventHandler, forwardRef, useCallback, useId, useRef } from 'react';
import { HexColorInput } from 'react-colorful';
import { Dropdown } from '../Dropdown';
import { useDropdownContext } from '../Dropdown/Dropdown';
import PopupArrow from '../Popup/PopupArrow';
import TextInputWrapper from '../TextInputWrapper';
import styles from './ColorInput.module.css';
import { buildBackgroundStyles, getTokenColorHex } from './helpers';
import { ColorInputProps, PopoverPickerProps, type SwatchProps } from './types';

/**
 * TODO DEV-26305:
 * - Add proper labels (international)
 */
const Swatch = forwardRef(function Swatch({
  onClick,
  color,
  triggerSwatch = false,
  'className': externalClassName,
  'aria-describedby': ariaDescribedBy,
  ...props
}: SwatchProps, ref: React.Ref<HTMLButtonElement>) {
  return <button {...props} className={classNames(styles.swatch, externalClassName, {
    [styles.activeSwatch as string]: triggerSwatch
  })} onClick={onClick} ref={ref} type="button"
  // This is the role of <input type='color' /> and we are trying to mimic some of its functionality
  aria-roledescription={triggerSwatch ? 'colour well' : 'button'} aria-describedby={ariaDescribedBy}>
      <div style={{
      background: buildBackgroundStyles(color)
    }} className={styles.swatchInner}></div>

      {/* DEV-26305 This is not really accessible, we should think about how to approach this */}
      <span className="sr-only">{color}</span>
    </button>;
});
function PopoverPicker({
  color,
  presetColors,
  onChange,
  hexInputLabel,
  label,
  id: id_,
  classNames: externalStyles,
  error,
  ...props
}: PopoverPickerProps) {
  const generatedId = useId();
  const id = id_ || props.name || generatedId;
  const modalContent = useRef(null);
  const popoverTriggerRef = useRef<HTMLButtonElement>(null);
  return <TextInputWrapper name={props.name} id={id} label={label} error={error} className={classNames(styles.picker, externalStyles?.label)} errorDisplay="border">
      <Dropdown className={styles.dropdown} trigger={<Swatch {...props} aria-describedby={`label-${id}`} id={id} color={color} ref={popoverTriggerRef} triggerSwatch className={classNames(externalStyles?.trigger, {
      [styles.errorSwatch!]: error
    })} />} Anchor={PopupArrow}>
        <div className={styles.popover} ref={modalContent}>
          <div className={styles.pickerSwatches}>
            {presetColors.map(presetColor => {
            return <SwatchItem {...props} key={presetColor} color={presetColor} onClick={() => {
              onChange(presetColor);
            }} className={classNames({
              [styles.activeSwatch!]: presetColor === color,
              [styles.inactiveSwatch as string]: presetColor !== color
            })} />;
          })}

            <DropdownHexColorInput id={id} color={color} onChange={onChange} alpha prefixed className={styles.input} />
          </div>
        </div>
      </Dropdown>
    </TextInputWrapper>;
}
function SwatchItem(t0) {
  const $ = _c(9);
  let onClick_;
  let props;
  if ($[0] !== t0) {
    ({
      onClick: onClick_,
      ...props
    } = t0);
    $[0] = t0;
    $[1] = onClick_;
    $[2] = props;
  } else {
    onClick_ = $[1];
    props = $[2];
  }
  const {
    hidePopup
  } = useDropdownContext();
  let t1;
  if ($[3] !== hidePopup || $[4] !== onClick_) {
    t1 = ev => {
      hidePopup();
      onClick_?.(ev);
    };
    $[3] = hidePopup;
    $[4] = onClick_;
    $[5] = t1;
  } else {
    t1 = $[5];
  }
  const onClick = t1;
  let t2;
  if ($[6] !== onClick || $[7] !== props) {
    t2 = <Swatch {...props} onClick={onClick} />;
    $[6] = onClick;
    $[7] = props;
    $[8] = t2;
  } else {
    t2 = $[8];
  }
  return t2;
}
type HexColorInputProps = Parameters<typeof HexColorInput>[0];
function DropdownHexColorInput(t0) {
  const $ = _c(9);
  let onKeyDown_;
  let props;
  if ($[0] !== t0) {
    ({
      onKeyDown: onKeyDown_,
      ...props
    } = t0);
    $[0] = t0;
    $[1] = onKeyDown_;
    $[2] = props;
  } else {
    onKeyDown_ = $[1];
    props = $[2];
  }
  const {
    hidePopup
  } = useDropdownContext();
  let t1;
  if ($[3] !== hidePopup || $[4] !== onKeyDown_) {
    t1 = ev => {
      onKeyDown_?.(ev);
      if (ev.key !== "Enter") {
        return;
      }
      hidePopup();
    };
    $[3] = hidePopup;
    $[4] = onKeyDown_;
    $[5] = t1;
  } else {
    t1 = $[5];
  }
  const onKeyDown = t1;
  let t2;
  if ($[6] !== onKeyDown || $[7] !== props) {
    t2 = <HexColorInput {...props} onKeyDown={onKeyDown} />;
    $[6] = onKeyDown;
    $[7] = props;
    $[8] = t2;
  } else {
    t2 = $[8];
  }
  return t2;
}
function ColorInput(t0) {
  const $ = _c(29);
  let hexInputLabel;
  let label;
  let onColorChange;
  let props;
  let t1;
  let value;
  if ($[0] !== t0) {
    ({
      onColorChange,
      transparentOption: t1,
      value,
      hexInputLabel,
      label,
      ...props
    } = t0);
    $[0] = t0;
    $[1] = hexInputLabel;
    $[2] = label;
    $[3] = onColorChange;
    $[4] = props;
    $[5] = t1;
    $[6] = value;
  } else {
    hexInputLabel = $[1];
    label = $[2];
    onColorChange = $[3];
    props = $[4];
    t1 = $[5];
    value = $[6];
  }
  const transparentOption = t1 === undefined ? false : t1;
  let t10;
  let t11;
  let t12;
  let t13;
  let t14;
  let t2;
  let t3;
  let t4;
  let t5;
  let t6;
  let t7;
  let t8;
  let t9;
  if ($[7] === Symbol.for("react.memo_cache_sentinel")) {
    t2 = getTokenColorHex("betterplace-color-light");
    t3 = getTokenColorHex("betterplace-color-green-500");
    t4 = getTokenColorHex("betterplace-color-green-700");
    t5 = getTokenColorHex("betterplace-color-green-900");
    t6 = getTokenColorHex("betterplace-color-yellow-400");
    t7 = getTokenColorHex("betterplace-me-color-orange-500");
    t8 = getTokenColorHex("betterplace-color-red-700");
    t9 = getTokenColorHex("betterplace-color-red-900");
    t10 = getTokenColorHex("betterplace-color-purple-400");
    t11 = getTokenColorHex("betterplace-color-purple-700");
    t12 = getTokenColorHex("betterplace-color-gray-500");
    t13 = getTokenColorHex("betterplace-color-gray-700");
    t14 = getTokenColorHex("betterplace-color-gray-900");
    $[7] = t10;
    $[8] = t11;
    $[9] = t12;
    $[10] = t13;
    $[11] = t14;
    $[12] = t2;
    $[13] = t3;
    $[14] = t4;
    $[15] = t5;
    $[16] = t6;
    $[17] = t7;
    $[18] = t8;
    $[19] = t9;
  } else {
    t10 = $[7];
    t11 = $[8];
    t12 = $[9];
    t13 = $[10];
    t14 = $[11];
    t2 = $[12];
    t3 = $[13];
    t4 = $[14];
    t5 = $[15];
    t6 = $[16];
    t7 = $[17];
    t8 = $[18];
    t9 = $[19];
  }
  let presetColors;
  if ($[20] !== transparentOption) {
    presetColors = [t2, t3, t4, t5, t6, t7, t8, t9, t10, t11, t12, t13, t14].filter(Boolean);
    if (transparentOption) {
      presetColors.push(`#${TRANSPARENT_COLOR_CODE}`);
    }
    $[20] = transparentOption;
    $[21] = presetColors;
  } else {
    presetColors = $[21];
  }
  const t15 = `#${value === "transparent" ? TRANSPARENT_COLOR_CODE : value}`;
  const t16 = presetColors as unknown as string[];
  let t17;
  if ($[22] !== hexInputLabel || $[23] !== label || $[24] !== onColorChange || $[25] !== props || $[26] !== t15 || $[27] !== t16) {
    t17 = <PopoverPicker {...props} color={t15} presetColors={t16} onChange={onColorChange} hexInputLabel={hexInputLabel} label={label} />;
    $[22] = hexInputLabel;
    $[23] = label;
    $[24] = onColorChange;
    $[25] = props;
    $[26] = t15;
    $[27] = t16;
    $[28] = t17;
  } else {
    t17 = $[28];
  }
  return t17;
}
export default ColorInput;